import { useCallback, useEffect, useRef, useState } from "react";
import { useShareSession } from "@jari-tools/shared";
import { BloomGarden } from "./BloomGarden";
import { QuestionStep } from "./QuestionStep";
import { QuestionsIntro } from "./QuestionsIntro";
import { ScenarioIntro } from "./ScenarioIntro";
import { ResultsIntro } from "./ResultsIntro";
import { ResultsSummary } from "./ResultsSummary";
import { BLOOM_LIKERT_COUNT, BLOOM_QUESTION_COUNT, shuffledBloomQuestions } from "../lib/questions";
import { computeThemeScores, themeScoreLabel } from "../lib/scoring";
import type { BloomAnswers, BloomQuestionWithTheme } from "../types/bloom-content";

type BloomPhase = "intro" | "questions" | "scenario-intro" | "results-intro" | "results";

function buildSummary(answers: BloomAnswers): string {
  return computeThemeScores(answers)
    .map((s) => `${s.label}: ${themeScoreLabel(s.score, s.maxScore)}`)
    .join("; ");
}

export function SharePlayApp() {
  const { submitResult, submitError, submitting } = useShareSession();
  const submittedRef = useRef(false);
  const lastAnswersRef = useRef<BloomAnswers | null>(null);

  const [phase, setPhase] = useState<BloomPhase>("intro");
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<BloomAnswers>({});
  const [questionSequence, setQuestionSequence] = useState<BloomQuestionWithTheme[]>(() =>
    shuffledBloomQuestions(),
  );

  const currentQuestion = questionSequence[questionIndex];

  const sendResult = useCallback(
    async (resultAnswers: BloomAnswers) => {
      lastAnswersRef.current = resultAnswers;
      if (submittedRef.current) return;
      submittedRef.current = true;
      try {
        await submitResult({
          completed: true,
          resultType: "bloom",
          data: {
            answers: resultAnswers,
            summary: buildSummary(resultAnswers),
          },
        });
      } catch {
        submittedRef.current = false;
      }
    },
    [submitResult],
  );

  const startScenarios = useCallback(() => {
    setPhase("questions");
    setQuestionIndex(BLOOM_LIKERT_COUNT);
  }, []);

  const handleSelect = useCallback(
    (value: number) => {
      if (!currentQuestion) return;

      const nextAnswers = { ...answers, [currentQuestion.id]: value };
      setAnswers(nextAnswers);

      if (questionIndex < BLOOM_LIKERT_COUNT - 1) {
        setQuestionIndex((i) => i + 1);
      } else if (questionIndex === BLOOM_LIKERT_COUNT - 1) {
        setPhase("scenario-intro");
      } else if (questionIndex < BLOOM_QUESTION_COUNT - 1) {
        setQuestionIndex((i) => i + 1);
      } else {
        setPhase("results-intro");
      }
    },
    [answers, currentQuestion, questionIndex],
  );

  const startResults = useCallback(() => {
    setPhase("results");
  }, []);

  const resetQuestionnaire = useCallback(() => {
    submittedRef.current = false;
    lastAnswersRef.current = null;
    setPhase("intro");
    setQuestionIndex(0);
    setAnswers({});
    setQuestionSequence(shuffledBloomQuestions());
  }, []);

  const startQuestions = useCallback(() => {
    setPhase("questions");
  }, []);

  const themeScores = phase === "results" ? computeThemeScores(answers) : [];
  const flowerCount = Object.keys(answers).length;

  useEffect(() => {
    if (phase === "results" && Object.keys(answers).length === BLOOM_QUESTION_COUNT) {
      void sendResult(answers);
    }
  }, [phase, answers, sendResult]);

  return (
    <main className="bloom-share-play">
      <BloomGarden flowerCount={flowerCount} dimmed={phase === "results"} />
      <div className="bloom-share-play__frame">
        {phase === "intro" ? <QuestionsIntro onStart={startQuestions} /> : null}

        {phase === "scenario-intro" ? <ScenarioIntro onContinue={startScenarios} /> : null}

        {phase === "questions" && currentQuestion ? (
          <QuestionStep
            question={currentQuestion}
            questionIndex={questionIndex}
            totalQuestions={BLOOM_QUESTION_COUNT}
            selectedValue={answers[currentQuestion.id]}
            onSelect={handleSelect}
            onBack={() => {}}
            canGoBack={false}
          />
        ) : null}

        {phase === "results-intro" ? <ResultsIntro onContinue={startResults} /> : null}

        {phase === "results" ? (
          <ResultsSummary scores={themeScores} onRestart={resetQuestionnaire} />
        ) : null}
      </div>

      {submitting ? (
        <p className="bloom-share-play__status" role="status">
          Resultaat verzenden…
        </p>
      ) : null}
      {submitError ? (
        <div className="bloom-share-play__error" role="alert">
          <p>Kan resultaat niet verzenden, probeer opnieuw.</p>
          <button
            type="button"
            className="jari-btn jari-btn--primary"
            onClick={() => {
              if (lastAnswersRef.current) {
                void sendResult(lastAnswersRef.current);
              }
            }}
            disabled={submitting || !lastAnswersRef.current}
          >
            Opnieuw proberen
          </button>
        </div>
      ) : null}
    </main>
  );
}
